import type { Vector2 } from './types';

/**
 * Create a new Vector2
 */
export function vec2(x: number = 0, y: number = 0): Vector2 {
  return { x, y };
}

/**
 * Add two vectors
 */
export function add(a: Vector2, b: Vector2): Vector2 {
  return { x: a.x + b.x, y: a.y + b.y };
}

/**
 * Subtract vector b from vector a
 */
export function subtract(a: Vector2, b: Vector2): Vector2 {
  return { x: a.x - b.x, y: a.y - b.y };
}

/**
 * Multiply vector by scalar
 */
export function multiply(v: Vector2, scalar: number): Vector2 {
  return { x: v.x * scalar, y: v.y * scalar };
}

/**
 * Get the length (magnitude) of a vector
 */
export function length(v: Vector2): number {
  return Math.sqrt(v.x * v.x + v.y * v.y);
}

/**
 * Get the squared length of a vector (faster than length)
 */
export function lengthSquared(v: Vector2): number {
  return v.x * v.x + v.y * v.y;
}

/**
 * Normalize a vector (make it unit length)
 */
export function normalize(v: Vector2): Vector2 {
  const len = length(v);
  if (len === 0) return { x: 0, y: 0 };
  return { x: v.x / len, y: v.y / len };
}

/**
 * Get distance between two points
 */
export function distance(a: Vector2, b: Vector2): number {
  return length(subtract(a, b));
}

/**
 * Get squared distance between two points (faster than distance)
 */
export function distanceSquared(a: Vector2, b: Vector2): number {
  return lengthSquared(subtract(a, b));
}

/**
 * Dot product of two vectors
 */
export function dot(a: Vector2, b: Vector2): number {
  return a.x * b.x + a.y * b.y;
}

/**
 * Cross product (returns scalar for 2D)
 */
export function cross(a: Vector2, b: Vector2): number {
  return a.x * b.y - a.y * b.x;
}

/**
 * Rotate a vector by angle (radians)
 */
export function rotate(v: Vector2, angle: number): Vector2 {
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  return {
    x: v.x * cos - v.y * sin,
    y: v.x * sin + v.y * cos,
  };
}

/**
 * Get angle of vector in radians
 */
export function angle(v: Vector2): number {
  return Math.atan2(v.y, v.x);
}

/**
 * Get angle from point a to point b
 */
export function angleBetween(a: Vector2, b: Vector2): number {
  return Math.atan2(b.y - a.y, b.x - a.x);
}

/**
 * Create vector from angle and magnitude
 */
export function fromAngle(angle: number, magnitude: number = 1): Vector2 {
  return {
    x: Math.cos(angle) * magnitude,
    y: Math.sin(angle) * magnitude,
  };
}

/**
 * Linear interpolation between two vectors
 */
export function lerp(a: Vector2, b: Vector2, t: number): Vector2 {
  return {
    x: a.x + (b.x - a.x) * t,
    y: a.y + (b.y - a.y) * t,
  };
}

/**
 * Linear interpolation between two numbers
 */
export function lerpNumber(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/**
 * Clamp a number between min and max
 */
export function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

/**
 * Normalize angle to be between -PI and PI
 */
export function normalizeAngle(angle: number): number {
  while (angle > Math.PI) angle -= Math.PI * 2;
  while (angle < -Math.PI) angle += Math.PI * 2;
  return angle;
}

/**
 * Get the shortest angle difference between two angles
 */
export function angleDifference(from: number, to: number): number {
  return normalizeAngle(to - from);
}

/**
 * Move angle towards target by max delta
 */
export function moveTowardsAngle(
  current: number,
  target: number,
  maxDelta: number
): number {
  const diff = angleDifference(current, target);
  if (Math.abs(diff) <= maxDelta) {
    return target;
  }
  return normalizeAngle(current + Math.sign(diff) * maxDelta);
}

/**
 * Check if two circles are colliding
 */
export function circleCollision(
  pos1: Vector2,
  radius1: number,
  pos2: Vector2,
  radius2: number
): boolean {
  const minDist = radius1 + radius2;
  return distanceSquared(pos1, pos2) <= minDist * minDist;
}

/**
 * Check if a point is inside a circle
 */
export function pointInCircle(
  point: Vector2,
  circlePos: Vector2,
  radius: number
): boolean {
  return distanceSquared(point, circlePos) <= radius * radius;
}

/**
 * Get random point inside a circle
 */
export function randomPointInCircle(
  center: Vector2,
  radius: number
): Vector2 {
  const angle = Math.random() * Math.PI * 2;
  const r = Math.sqrt(Math.random()) * radius;
  return {
    x: center.x + Math.cos(angle) * r,
    y: center.y + Math.sin(angle) * r,
  };
}

/**
 * Get random number between min and max
 */
export function randomRange(min: number, max: number): number {
  return min + Math.random() * (max - min);
}

/**
 * Get random integer between min and max (inclusive)
 */
export function randomInt(min: number, max: number): number {
  return Math.floor(randomRange(min, max + 1));
}

/**
 * Pick random element from array
 */
export function randomPick<T>(array: readonly T[]): T | undefined {
  if (array.length === 0) return undefined;
  return array[Math.floor(Math.random() * array.length)];
}
